import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Imagekit } from "../../Pages/CommonPages/ImageKit";
import { createSlug } from "../../Utils/HelperFunctions";
import useIsDesktop from "../../Hooks/useIsDesktop";

/**
 * KnowYourCraftRow
 *
 * "Know your craft" education shelf — article teaser cards fed from
 * get_home_page (knowYourCrafts.articles). Cards open the article page;
 * "See all" → /journal.
 *
 * @param {Array}  articles - [{ id, title, category, heroImage, readTimeMins }] — ONE PAGE
 * @param {number} total    - totalElements from the paginated envelope
 */
export default function KnowYourCraftRow({ articles = [], total }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  if (!articles?.length) return null;

  // Cap visible cards per breakpoint (mobile 4 / desktop 7). `articles` is ONE
  // PAGE of get_home_page (pageSize hardcoded to 4), so the old
  // `articles.length > cap` test could never be true — "is there more?" is only
  // answerable from the envelope's totalElements. Compared against the cards on
  // screen rather than `cap` so nothing is stranded while the page stays at 4.
  // See CraftClusterRow for the full reasoning.
  const cap = isDesktop ? 7 : 4;
  const visibleArticles = articles.slice(0, cap);
  const totalCount = total ?? articles.length; // fall back if envelope is absent
  const showSeeAll = totalCount > visibleArticles.length;

  return (
    <section className="pt-[8px] pb-[12px]">
      <div className="px-4 pb-[11px]">
        <h2 className="text-[17px] font-[500] text-ink-900 tracking-[-0.01em]">
          <Trans
            i18nKey="knowCraft.heading"
            components={[<span className="text-coral-ink" />]}
          />
        </h2>
        <div className="text-[11px] text-ink-500 mt-[3px]">
          {t("knowCraft.sub")}
        </div>
      </div>

      <div className="flex gap-[11px] overflow-x-auto no-scrollbar pt-[2px] px-4 pb-[8px] snap-x snap-mandatory scroll-pl-4">
        {visibleArticles.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => navigate(`/journal/${createSlug(a.title)}/${a.id}`)}
            className="flex-none w-[198px] snap-start bg-white border border-wc-border rounded-lg overflow-hidden text-left"
            style={{ boxShadow: "0 4px 18px rgba(31,33,46,.08)" }}
          >
            <div className="w-full aspect-[16/10] bg-[#F5F5F5]">
              <Imagekit
                IMGName={a.heroImage}
                className="w-full h-full object-cover object-top"
                eager
              />
            </div>
            <div className="px-[11px] pt-[10px] pb-[12px]">
              {a.category && (
                <span className="inline-block text-[8.5px] font-[500] tracking-[0.04em] uppercase px-[8px] py-[3px] rounded-pill bg-surface-peach text-coral-ink mb-[6px]">
                  {a.category}
                </span>
              )}
              <div className="text-[12px] font-[500] text-ink-900 leading-[1.3] h-[46px] overflow-hidden">
                {a.title}
              </div>
              {a.readTimeMins ? (
                <div className="text-[9.5px] text-ink-400 mt-[6px] font-[500]">
                  {a.readTimeMins} min read
                </div>
              ) : null}
            </div>
          </button>
        ))}

        {/* See all — only when articles overflow the visible run */}
        {showSeeAll && (
          <button
            type="button"
            onClick={() => navigate("/journal")}
            className="flex-none w-[198px] snap-start flex items-center justify-center min-h-[150px] bg-[#F4F5FF] rounded-lg"
          >
            <span className="text-[12px] font-[500] text-s-primary text-center px-[12px]">
              {t("knowCraft.seeAll")}
            </span>
          </button>
        )}
      </div>
    </section>
  );
}
